import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useStore from '../store/useStore';
import { metricsAPI, datasetsAPI } from '../services/api';
import FreshnessBadge from './widgets/FreshnessBadge';
import {
  FRESHNESS_LABEL, CADENCE_LABEL, WIDGET_TYPE_LABEL, describeValue, formatDateTime, formatNumber, datasetPath, metricPath, definedBy,
} from './data-sources/dataSourceFormat';
import '../styles/DatasetManagement.css';
import '../styles/DataSources.css';
import '../styles/DashboardEditor.css';

/**
 * Everything a company's sync tools push, in one list: live metrics (current
 * values, updated in real time) and datasets (rows kept for reporting). Open
 * either to see its detail, history and what is built on it.
 */
const DataSourcesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentCompany = useStore((state) => state.currentCompany);
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const companyId = currentCompany?.id;
  const filter = ['metric', 'dataset'].includes(searchParams.get('kind')) ? searchParams.get('kind') : 'all';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(location.state?.notice || '');

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError('');
      const [defs, latest, datasets] = await Promise.all([
        metricsAPI.getDefinitions(companyId),
        metricsAPI.getLatestAll(companyId),
        datasetsAPI.list(companyId),
      ]);
      const latestByKey = new Map((latest?.success ? latest.data : []).map((m) => [m.metric_key, m]));
      const metricRows = (defs?.success ? defs.data : []).map((d) => {
        const last = latestByKey.get(d.metric_key);
        return {
          id: `m-${d.id}`,
          kind: 'metric',
          name: d.display_name || d.metric_key,
          key: d.metric_key,
          detail: WIDGET_TYPE_LABEL[d.widget_type] || d.widget_type,
          lastReceived: last?.timestamp || null,
          cadence: 'Live',
          size: last ? describeValue(last.value) : '—',
          template: null,
          freshness: last?.timestamp ? 'fresh' : 'never',
          trackedAsKpi: Boolean(d.rollup_aggregation),
          href: metricPath(d.metric_key),
        };
      });
      const datasetRows = (datasets?.success ? datasets.data : []).map((d) => ({
        id: `d-${d.id}`,
        kind: 'dataset',
        name: d.name,
        key: d.name,
        detail: `${formatNumber(d.columnCount)} columns${d.relationshipCount ? ` · ${d.relationshipCount} relationship${d.relationshipCount === 1 ? '' : 's'}` : ''}`,
        lastReceived: d.lastSyncedAt,
        cadence: CADENCE_LABEL[d.cadence] || d.cadence,
        size: `${formatNumber(d.rowCount)} rows`,
        template: definedBy(d),
        freshness: d.freshness,
        loading: d.openLoads > 0,
        href: datasetPath(d.name),
      }));
      setRows([...metricRows, ...datasetRows]);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load data sources');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('kind'); else next.set('kind', value);
    setSearchParams(next, { replace: true });
  };

  const shown = rows.filter((r) => filter === 'all' || r.kind === filter);
  const counts = { metric: rows.filter((r) => r.kind === 'metric').length, dataset: rows.filter((r) => r.kind === 'dataset').length };

  return (
    <div className="dataset-management">
      <div className="page-header">
        <div>
          <h2>Data sources</h2>
          {currentCompany && <p className="page-subtitle">Everything pushed to <strong>{currentCompany.name}</strong> · {counts.metric} live metric{counts.metric === 1 ? '' : 's'}, {counts.dataset} dataset{counts.dataset === 1 ? '' : 's'}</p>}
        </div>
        <div className="page-actions">
          <button className="btn-secondary" onClick={load}>Refresh</button>
          {isAdmin && <Link className="btn-secondary" to="/admin/data-sources/packs">Manage packs</Link>}
          {isAdmin && <Link className="btn-primary" to="/admin/data-sources/add">+ Add data source</Link>}
        </div>
      </div>

      <div className="source-filter" role="tablist" aria-label="Filter data sources">
        {[['all', 'All', rows.length], ['metric', 'Live metrics', counts.metric], ['dataset', 'Datasets', counts.dataset]].map(([value, label, n]) => (
          <button key={value} type="button" role="tab" aria-selected={filter === value} className={`library-category ${filter === value ? 'active' : ''}`} onClick={() => setFilter(value)}>
            {label} <span className="library-category-count">{n}</span>
          </button>
        ))}
      </div>

      {error && <div className="error-message" role="alert">{error}</div>}
      {notice && <div className="success-message" role="status">{notice}<button className="error-dismiss" onClick={() => setNotice('')} aria-label="Dismiss">×</button></div>}

      {loading && rows.length === 0 ? <div className="loading-spinner" /> : (
        <div className="datasets-table-container">
          <table className="datasets-table source-table">
            <thead>
              <tr><th>Name</th><th>Kind</th><th>Value / size</th><th>Expected</th><th>Last received</th><th>Defined by</th><th>Freshness</th></tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={7} className="dataset-empty-state">
                  {rows.length === 0
                    ? <>No data sources yet. <Link to="/admin/data-sources/add">Add a live metric or a dataset</Link>, or <Link to="/admin/data-sources/packs">install a pack</Link> for a system you use.</>
                    : 'Nothing of this kind yet.'}
                </td></tr>
              )}
              {shown.map((r) => (
                <tr key={r.id} className="source-row" onClick={() => navigate(r.href)}>
                  <td>
                    <Link to={r.href} onClick={(e) => e.stopPropagation()}><strong>{r.name}</strong></Link>
                    {r.loading && <span className="dataset-badge info">loading</span>}
                    <div className="kpi-key">{r.kind === 'metric' && r.key !== r.name ? <><code>{r.key}</code> · </> : null}{r.detail}</div>
                  </td>
                  <td>
                    <span className={`dataset-badge ${r.kind === 'metric' ? 'info' : 'ok'}`} style={{ marginLeft: 0 }}>{r.kind === 'metric' ? 'live metric' : 'dataset'}</span>
                    {r.trackedAsKpi && <span className="dataset-badge ok" title="Saved daily, weekly or monthly into kpi_values">KPI</span>}
                  </td>
                  <td>{r.size}</td>
                  <td>{r.cadence}</td>
                  <td>{formatDateTime(r.lastReceived)}</td>
                  <td>{r.template || '—'}</td>
                  <td>
                    {r.kind === 'metric'
                      ? <FreshnessBadge kind="live" at={r.lastReceived} staleAfterMs={5 * 60 * 1000} />
                      : <span className={`dataset-badge freshness-${r.freshness}`} style={{ marginLeft: 0 }}>{FRESHNESS_LABEL[r.freshness] || r.freshness}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};

export default DataSourcesPage;
